import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
/* eslint-disable import/no-internal-modules */
import { toIdFromHex } from '#~/api/utils.js';
import { nats } from '#~/nats/index.js';
import { RoleTranslationMap } from '#~/room/components/user-invite/constants.js';
import { Paths } from '#~/routes/constants.js';
import { Account } from '#~/routes/scaffold/header/account/index.js';
import { InviteError } from './components/index.js';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import { Centered } from '@studio/design-system/overlay';
import { getCurrentVendor, Vendor, vendorName } from '@studio/design-system/themes/vendor.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import { AgentRegistryGetInvite } from '@tryiris-ai/mailroom-helper/agentRegistry';
import { clsx } from 'cn';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'cn';
import { Logo } from '@studio/design-system/logo';
import T from '@studio/design-system/themes';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES, trackAuthLoginFailed } from '#~/analytics/index.js';
import {
  beginAuthJourney,
  buildHostedAuth0ExtParams,
  makeAuthJourneyAppState
} from '#~/auth/index.js';

/**
 * PreRedeem component.
 *
 * @returns The element.
 */
export const PreRedeem = (): ReactElement => {
  const params = useParams();

  // BootOverlay dismissal for this route is owned by <BootReadyLayout> (router.tsx),
  // which publishes boot.isRootReady on mount; this route no longer does (ENG-4186).

  const inviteIdHex = params.inviteId!;
  const redemptionTokenHex = params.redemptionToken!;

  const inviteId = toIdFromHex(inviteIdHex)!;
  const redemptionToken = toIdFromHex(redemptionTokenHex)!;

  const [loading, setLoading] = useState(true);

  const [errorCode, setErrorCode] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { track } = useAnalytics();

  const [details, setDetails] = useState<{
    inviteEmail: string | undefined;
    inviterName: string | undefined;
    orgName: string | undefined;
    orgPhotoUrl: string | undefined;
    role: string | undefined;
    roomName: string | undefined;
    userExists: boolean;
  }>();

  useEffect(() => {
    const fn = async () => {
      await nats.forceUnauthenticatedConnect();

      try {
        const resp = await AgentRegistryGetInvite.request(nats.cloudConnection(), {
          id: inviteId,
          redemptionToken
        });

        if (resp.error?.code) {
          setErrorCode(resp.error.code);
          setErrorMessage(resp.error.message);
          setLoading(false);
          return;
        }

        if (resp.invite?.error?.code) {
          setErrorCode(resp.invite.error.code);
          setErrorMessage(resp.invite.error.message);
          setLoading(false);
          return;
        }

        let role: string | undefined;

        if (resp.invite?.role !== undefined) {
          role = RoleTranslationMap[resp.invite.role]!;
        }

        setDetails({
          orgName: resp.organizationName,
          orgPhotoUrl: resp.organizationPhotoUrl,
          inviteEmail: resp.invite?.inviteeEmail,
          roomName: resp.roomName,
          role,
          inviterName: resp.inviterName,
          userExists: resp.userExists
        });

        setLoading(false);
      } catch (error: unknown) {
        setLoading(false);
        setErrorCode(500);
        setErrorMessage(error instanceof Error ? error.message : undefined);
      }
    };

    void fn();
  }, []);

  const { loginWithRedirect } = useAuth0();
  const { inviteEmail, roomName, userExists } = details ?? {};

  const handleAccept = useLatestFn(() => {
    let authorizationParameters: { login_hint?: string; screen_hint?: string } = {
      screen_hint: userExists ? 'login' : 'signup'
    };
    const journey = beginAuthJourney(userExists ? 'studio_login' : 'studio_register');

    if (inviteEmail) {
      authorizationParameters = { ...authorizationParameters, login_hint: inviteEmail };
    }

    track(EVENT_NAMES.COLLAB_OPEN_INVITE, EVENT_CATEGORIES.COLLABORATION, {
      roomName: details?.roomName ?? '',
      orgName: details?.orgName ?? ''
    });

    void loginWithRedirect({
      appState: makeAuthJourneyAppState(
        Paths.invite.postRedeem.resolve(inviteIdHex, redemptionTokenHex),
        journey
      ),
      authorizationParams: {
        ...authorizationParameters,
        ...buildHostedAuth0ExtParams(journey)
      }
    }).catch((error: Error) => {
      // ENG-4348 stage 3: dual-emits auth_login_failed + legacy oauth_error
      // through the shared helper (journey props injected there) so this site
      // can't drift from the canonical pair during the parity window.
      trackAuthLoginFailed(track, journey, { error_message: error.message });
    });
  });

  useRenderedBootHumanWait(!loading);

  const logoMap = {
    [Vendor.IRIS]: '/assets/icons/iris_badge.png',
    [Vendor.PTZOPTICS]: '/assets/icons/hive_badge.png'
  };

  const organizationLogo = (details?.orgPhotoUrl || undefined) ?? logoMap[getCurrentVendor()];
  const isPtzoptics = vendorName().toLowerCase() === 'hive';

  return (
    <div>
      <div className='relative z-10 flex justify-end p-16'>
        <Account />
      </div>
      <Centered className='backdrop-blur-sm'>
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className='fixed left-0 right-0 top-0 flex flex-col items-center justify-center pt-48'>
              <Logo size='2x' />
            </div>
            <div
              className={twMerge(
                clsx(
                  'max-h-[650px] w-[450px] rounded-12 border-t-12 border-primary bg-light p-32 pt-12 text-foreground',
                  {
                    'border-error': !!errorCode,
                    'border-primary': !errorCode
                  }
                )
              )}
              data-theme={`${T.getTheme('light')}`}
            >
              {errorCode ? (
                <InviteError code={errorCode} errorMessage={errorMessage} />
              ) : (
                <div className='p-32 text-center' data-theme='light'>
                  <img
                    src={organizationLogo}
                    alt={details?.orgName ? `${details.orgName} logo` : `${vendorName()} logo`}
                    className='mx-auto w-[96px]'
                  />
                  <h1 className='mt-12'>Join {roomName}</h1>

                  <p className='mb-32'>
                    You were invited to join <span className='font-demibold'>{roomName}</span> on{' '}
                    {vendorName()}. Accept the invitation below to join the Studio.
                  </p>

                  <Button
                    className='mx-auto'
                    onClick={() => {
                      handleAccept();
                    }}
                    size='xl'
                    data-theme={`${T.getTheme('light')}`}
                  >
                    Accept Invite
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        {isPtzoptics && (
          <div className='flex flex-col items-center justify-center pt-[37%]'>
            <Logo logoSrc={'/assets/powered_by.png'} size='2x' />
          </div>
        )}
      </Centered>
    </div>
  );
};
